import React, { useMemo, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, UncontrolledTooltip } from 'reactstrap'
import AddPeopleModal from '../IncidentModals/AddPeopleModal'
import AddPartyEntityModal from '../IncidentModals/AddPartyEntityModal'
import PersonInfo from './PersonInfo'
import toTitleCase from '../../Functions/toTitleCase'
import { SEAT_ARRANGEMENT } from '../../Constants'
import seatArrangementImage from './seat-arrangement.png'
import './SeatArrangement.css'

const DRIVER_ROLES = ['VEHICLE_DRIVER', 'UNAUTHORIZED_DRIVER', 'EMPLOYEE', 'VENDOR']
const PASSENGER_SEAT_KEY_SET = new Set(SEAT_ARRANGEMENT.map(seat => seat.value))

const getPassengerPositions = (metadata) => {
    if (!metadata) return {}
    if (typeof metadata === 'string') {
        try {
            return JSON.parse(metadata)?.passenger_positions || {}
        } catch {
            return {}
        }
    }
    return metadata.passenger_positions || {}
}

const getInitials = (name = '') =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase() || '?'

const SeatArrangement = ({ party, refetchQuery }) => {
    const [openModal, setOpenModal] = useState('')
    const hasRental = Boolean(party?.incident?.rental?.id)

    const peopleEdges = useMemo(
        () => party?.incidentpartypeopleSet?.edges ?? [],
        [party?.incidentpartypeopleSet?.edges]
    )

    const driverPeople = useMemo(
        () => peopleEdges.filter(p => DRIVER_ROLES.includes(p.node.role)),
        [peopleEdges]
    )

    const passengerPeople = useMemo(
        () => peopleEdges.filter(p => p.node.role === 'VEHICLE_PASSENGER'),
        [peopleEdges]
    )

    const hasVehicleDriver = useMemo(
        () => peopleEdges.some(p => p.node.role === 'VEHICLE_DRIVER'),
        [peopleEdges]
    )

    const passengerPositions = useMemo(
        () => getPassengerPositions(party?.metadata),
        [party?.metadata]
    )

    const resolvedPassengersBySeat = useMemo(() => {
        const passengerById = new Map(
            passengerPeople.map(person => [String(person.node.pk ?? person.node.id), person])
        )

        const usedPassengerIds = new Set()
        const result = {}

        Object.entries(passengerPositions).forEach(([passengerId, seatKey]) => {
            const normalizedId = String(passengerId)
            if (PASSENGER_SEAT_KEY_SET.has(seatKey) && passengerById.has(normalizedId) && !usedPassengerIds.has(normalizedId)) {
                result[seatKey] = passengerById.get(normalizedId)
                usedPassengerIds.add(normalizedId)
            }
        })

        return result
    }, [passengerPeople, passengerPositions])

    const assignedPassengerIds = useMemo(
        () => new Set(Object.values(resolvedPassengersBySeat).map(person => String(person.node.pk ?? person.node.id))),
        [resolvedPassengersBySeat]
    )

    const unassignedPassengers = useMemo(
        () => passengerPeople.filter(person => !assignedPassengerIds.has(String(person.node.pk ?? person.node.id))),
        [passengerPeople, assignedPassengerIds]
    )

    const seats = useMemo(() => {
        const passengerSeats = SEAT_ARRANGEMENT.map(seat => {
            return {
                key: seat.value,
                label: seat?.label || seat.value,
                person: resolvedPassengersBySeat[seat.value],
            }
        })

        return [
            ...passengerSeats,
            { key: 'D1', label: 'Driver', person: driverPeople[0] },
        ]
    }, [driverPeople, resolvedPassengersBySeat])

    const handleSeatClick = (seat) => {
        if (seat.person?.node?.id) {
            setOpenModal(`view_${seat.key}`)
            return
        }

        if (seat.key === 'D1') {
            setOpenModal('addDriver_D1')
        } else {
            setOpenModal(`addPassenger_${seat.key}`)
        }
    }

    return (
        <div className="seat-arrangement">
            <div className="seat-arrangement__layout">
                {unassignedPassengers.length > 0 && (
                    <div className="seat-arrangement__unassigned">
                        <div className="seat-arrangement__unassigned-header">
                            <span className="seat-arrangement__unassigned-title">{unassignedPassengers.length} Unassigned Passengers</span>
                        </div>
                        <div className="seat-arrangement__unassigned-list">
                            {unassignedPassengers.map(person => (
                                <button
                                    key={`unassigned_${person.node.id}`}
                                    type="button"
                                    className="seat-arrangement__unassigned-card"
                                    onClick={() => setOpenModal(`view_unassigned_${person.node.id}`)}
                                >
                                    <span className="seat-arrangement__avatar" aria-hidden="true">
                                        {getInitials(person.node.name)}
                                    </span>
                                    <span className="seat-arrangement__card-text">
                                        <span className="seat-arrangement__card-name">{person.node.name}</span>
                                        <span className="seat-arrangement__card-role">Passenger</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="seat-arrangement__canvas">
                    <img src={seatArrangementImage} alt="Seat arrangement" className="seat-arrangement__image" />
                    {seats.map(seat => (
                        <button
                            key={seat.key}
                            type="button"
                            className={`seat-arrangement__seat seat-arrangement__seat--${seat.key.toLowerCase()} ${seat.person ? 'is-filled' : ''}`}
                            onClick={() => handleSeatClick(seat)}
                            id={seat.key}
                        >
                            <span className="seat-arrangement__seat-label">
                                {seat.person?.node?.name || seat.label}
                            </span>
                            <UncontrolledTooltip target={seat.key}>
                                {seat.person ? 'View Details' : `Add ${seat.label}`}
                            </UncontrolledTooltip>
                        </button>
                    ))}
                </div>
            </div>

            <div className="seat-arrangement__legend">
                <div className="seat-arrangement__legend-item">
                    <span className="seat-arrangement__legend-dot seat-arrangement__legend-dot--available" />
                    Available Seat
                </div>
                <div className="seat-arrangement__legend-item">
                    <span className="seat-arrangement__legend-dot seat-arrangement__legend-dot--occupied" />
                    Occupied Seat
                </div>
            </div>

            {seats.map(
                seat =>
                    seat.person?.node?.id && (
                        <Modal key={`modal_${seat.key}`} isOpen={openModal === `view_${seat.key}`} toggle={() => setOpenModal('')} size="lg">
                            <ModalHeader>{toTitleCase(seat.person.node.role)} - {seat.person.node.name}</ModalHeader>
                            <ModalBody>
                                <PersonInfo person={seat.person.node} refetchQuery={refetchQuery} isDefault={party.isDefault} hasRental={hasRental} colSize={6} passengerPosition={seat.key.startsWith('P') ? seat.key : undefined} />
                            </ModalBody>
                            <ModalFooter>
                                <button type="button" className="btn btn-secondary" onClick={() => setOpenModal('')}>Close</button>
                            </ModalFooter>
                        </Modal>
                    )
            )}

            {unassignedPassengers.map(person => (
                <Modal
                    key={`modal_unassigned_${person.node.id}`}
                    isOpen={openModal === `view_unassigned_${person.node.id}`}
                    toggle={() => setOpenModal('')}
                    size="lg"
                >
                    <ModalHeader>{toTitleCase(person.node.role)} - {person.node.name}</ModalHeader>
                    <ModalBody>
                        <PersonInfo person={person.node} refetchQuery={refetchQuery} isDefault={party.isDefault} hasRental={hasRental} colSize={6} />
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-secondary" onClick={() => setOpenModal('')}>Close</button>
                    </ModalFooter>
                </Modal>
            ))}

            {openModal === 'addDriver_D1' &&
                (party.isDefault ? (
                    <AddPeopleModal
                        isOpen={true}
                        toggle={() => setOpenModal('')}
                        partyType="VEHICLE"
                        roles={!hasVehicleDriver ? DRIVER_ROLES : DRIVER_ROLES.filter(r => r !== 'VEHICLE_DRIVER')}
                        partyId={party.id}
                        refetchQuery={refetchQuery}
                    />
                ) : (
                    <AddPeopleModal
                        isOpen={true}
                        toggle={() => setOpenModal('')}
                        partyType="VEHICLE"
                        role="VEHICLE_DRIVER"
                        partyId={party.id}
                        refetchQuery={refetchQuery}
                    />
                ))}

            {openModal.startsWith('addPassenger_') && (
                <AddPartyEntityModal
                    isOpen={true}
                    toggle={() => setOpenModal('')}
                    entity="VEHICLE_PASSENGER"
                    passengerPosition={openModal.replace('addPassenger_', '')}
                    partyId={party?.id}
                    refetchQuery={refetchQuery}
                />
            )}
        </div>
    )
}

export default SeatArrangement
